import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from '@tanstack/react-router';
import client from '@/api/client';
import type { RentStatusEntry, PageResponse, RentStatus, PaymentSummary } from '@/api/types';
import { useState } from 'react';
import { AlertTriangle, Users, IndianRupee } from 'lucide-react';
import { PendingVerificationsPanel } from '@/features/rent/PendingVerificationsPanel';
import { ReceiptAttachField } from '@/features/rent/ReceiptAttachField';
import { uploadReceipt } from '@/features/rent/receiptApi';
import { Select } from '@/components/ui/Select';
import { Pagination } from '@/components/ui/Pagination';
import { usePagination, paginateArray } from '@/hooks/usePagination';
import { rentStatusLabel, paymentMethodLabel } from '@/lib/labels';

const STATUS_COLORS: Record<RentStatus, string> = {
  PENDING:  'bg-amber-100 text-amber-800',
  PAID:     'bg-green-100 text-green-800',
  PARTIAL:  'bg-blue-100 text-blue-800',
  OVERDUE:  'bg-red-100 text-red-700',
  WAIVED:   'bg-gray-100 text-gray-600',
};

function fmt(n: number) {
  return '₹' + n.toLocaleString('en-IN');
}

function todayStr() { return new Date().toISOString().split('T')[0]; }
function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

type Tab = 'entries' | 'history' | 'verifications';

export function OwnerRentPage() {
  const { t } = useTranslation();
  const { id: hostelId } = useParams({ from: '/protected/owner/hostels/$id/rent' });
  const qc = useQueryClient();
  const navigate = useNavigate();

  function goToTenant(tenantId: string) {
    void navigate({
      to: '/owner/hostels/$id/tenants/$tenantId',
      params: { id: hostelId, tenantId },
      search: { tab: 'profile' },
    });
  }
  const [tab, setTab] = useState<Tab>('entries');
  const [statusFilter, setStatusFilter] = useState<RentStatus | ''>('');
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payingTenantId, setPayingTenantId] = useState<string>('');
  const [payForm, setPayForm] = useState({ amount: '', paymentMethod: 'CASH', transactionRef: '' });
  const [payReceiptFile, setPayReceiptFile] = useState<File | null>(null);
  const payRefRequired = payForm.paymentMethod === 'UPI' || payForm.paymentMethod === 'BANK_TRANSFER';

  // Rent entries date-range filter (due date)
  const [entriesFromDate, setEntriesFromDate] = useState('');
  const [entriesToDate, setEntriesToDate] = useState('');

  // Payment history filter
  const [fromDate, setFromDate] = useState(firstOfMonthStr());
  const [toDate, setToDate] = useState(todayStr());

  const entriesPagination = usePagination();
  const historyPagination = usePagination();

  const { data, isLoading } = useQuery<PageResponse<RentStatusEntry>>({
    queryKey: ['rent', hostelId, statusFilter, entriesFromDate, entriesToDate, entriesPagination.page],
    queryFn: () => {
      const p = new URLSearchParams({ hostelId, page: String(entriesPagination.page), size: String(entriesPagination.size) });
      if (statusFilter) p.set('status', statusFilter);
      if (entriesFromDate) p.set('from', entriesFromDate);
      if (entriesToDate) p.set('to', entriesToDate);
      return client.get(`/api/rent?${p}`).then(r => r as unknown as PageResponse<RentStatusEntry>);
    },
  });

  const { data: paymentHistory = [], isLoading: historyLoading } = useQuery<PaymentSummary[]>({
    queryKey: ['payment-history', hostelId, fromDate, toDate],
    queryFn: () =>
      client.get(`/api/rent/payment-history?hostelId=${hostelId}&from=${fromDate}&to=${toDate}`)
            .then(r => r as unknown as PaymentSummary[]),
    enabled: tab === 'history' && !!fromDate && !!toDate,
  });

  const pagedPaymentHistory = paginateArray(paymentHistory, historyPagination.page, historyPagination.size);

  const payMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: { amount: number; paymentMethod: string; transactionRef?: string } }) => {
      let receiptUrl: string | undefined;
      if (payReceiptFile) {
        receiptUrl = await uploadReceipt(payReceiptFile, hostelId, payingTenantId);
      }
      return client.post(`/api/rent/${id}/pay`, { ...body, receiptUrl });
    },
    onSuccess: () => {
      setPayingId(null);
      setPayReceiptFile(null);
      void qc.invalidateQueries({ queryKey: ['rent'] });
      void qc.invalidateQueries({ queryKey: ['payment-history'] });
    },
  });

  const entries = data?.content ?? [];

  // Group entries by room, rooms sorted ascending (A1, A2, 401, 402 …)
  const grouped: Map<string, typeof entries> = new Map();
  const sortedEntries = [...entries].sort((a, b) =>
    (a.roomNumber ?? '').localeCompare(b.roomNumber ?? '', undefined, { numeric: true, sensitivity: 'base' })
  );
  for (const e of sortedEntries) {
    const room = e.roomNumber ?? '—';
    if (!grouped.has(room)) grouped.set(room, []);
    grouped.get(room)!.push(e);
  }
  // Pending amount banner
  const pendingEntries = entries.filter(e => e.status === 'PENDING' || e.status === 'PARTIAL' || e.status === 'OVERDUE');
  const overdueEntries = entries.filter(e => e.status === 'OVERDUE');
  const totalPending   = pendingEntries.reduce((s, e) => s + (Number(e.amountDue) - Number(e.amountPaid)), 0);

  // Payment history stats
  const totalCollected = paymentHistory.reduce((s, p) => s + Number(p.amount), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/owner/hostels/$id" params={{ id: hostelId }} search={{ bedStatus: '' }} className="text-teal-600 text-sm hover:underline">← {t('room.title')}</Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-2xl font-bold text-gray-900">{t('rent.title')}</h1>
      </div>

      {/* Pending amount alert */}
      {totalPending > 0 && (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
          overdueEntries.length > 0
            ? 'bg-red-50 border-red-200 text-red-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <span className="font-semibold">{t('owner.amountPending', '{{amount}} pending', { amount: fmt(totalPending) })}</span>
            {' '}{t('owner.acrossRentEntries', 'across {{count}} rent entries', { count: pendingEntries.length })}
            {overdueEntries.length > 0 && (
              <span className="ml-2 font-semibold text-red-700">
                · {t('owner.overdueCount', '{{count}} overdue', { count: overdueEntries.length })}
              </span>
            )}
          </div>
          <button
            onClick={() => { setStatusFilter('OVERDUE'); entriesPagination.resetPage(); }}
            className="text-xs font-medium underline whitespace-nowrap"
          >
            {t('owner.viewOverdue', 'View overdue')}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex border-b border-gray-100">
          {([
            { key: 'entries', label: t('owner.rentEntries', 'Rent Entries') },
            { key: 'history', label: t('owner.paymentHistory', 'Payment History') },
            { key: 'verifications', label: t('owner.pendingVerifications', 'Pending Verifications') },
          ] as { key: Tab; label: string }[]).map(tabItem => (
            <button
              key={tabItem.key}
              onClick={() => setTab(tabItem.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === tabItem.key
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        {/* ── Rent Entries Tab ── */}
        {tab === 'entries' && (
          <div>
            <div className="flex flex-wrap items-end justify-between gap-3 px-4 py-3 border-b border-gray-50">
              <p className="text-xs text-gray-400">{t('owner.entriesRoomsCount', '{{entries}} entries · {{rooms}} rooms', { entries: entries.length, rooms: grouped.size })}</p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('owner.fromLabel', 'From')}</label>
                  <input
                    type="date"
                    value={entriesFromDate}
                    onChange={e => { setEntriesFromDate(e.target.value); entriesPagination.resetPage(); }}
                    className="h-9 border border-gray-300 rounded-lg px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('owner.toLabel', 'To')}</label>
                  <input
                    type="date"
                    value={entriesToDate}
                    onChange={e => { setEntriesToDate(e.target.value); entriesPagination.resetPage(); }}
                    className="h-9 border border-gray-300 rounded-lg px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.status')}</label>
                  <Select
                    value={statusFilter}
                    onChange={e => { setStatusFilter(e.target.value as RentStatus | ''); entriesPagination.resetPage(); }}
                    className="w-auto h-9"
                    options={[
                      { value: '', label: t('owner.allStatus', 'All Status') },
                      ...(['PENDING', 'PAID', 'PARTIAL', 'OVERDUE', 'WAIVED'] as RentStatus[]).map(s => ({ value: s, label: rentStatusLabel(t, s) })),
                    ]}
                  />
                </div>
              </div>
            </div>
            {isLoading ? (
              <div className="p-10 text-center">
                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-400 text-sm">{t('common.loading')}</p>
              </div>
            ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">{t('owner.tenant', 'Tenant')}</th>
                  <th className="px-4 py-3 text-left">{t('common.room')}</th>
                  <th className="px-4 py-3 text-left">{t('owner.month', 'Month')}</th>
                  <th className="px-4 py-3 text-left">{t('rent.dueDate')}</th>
                  <th className="px-4 py-3 text-right">{t('rent.amountDue')}</th>
                  <th className="px-4 py-3 text-right">{t('owner.paid', 'Paid')}</th>
                  <th className="px-4 py-3 text-right">{t('rent.pending')}</th>
                  <th className="px-4 py-3 text-left">{t('common.status')}</th>
                  <th className="px-4 py-3 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">{t('rent.noEntries')}</td></tr>
                )}
                {[...grouped.entries()].map(([room, roomEntries]) => {
                  const visible = statusFilter ? roomEntries.filter(e => e.status === statusFilter) : roomEntries;
                  if (visible.length === 0) return null;
                  return (
                    <>
                      {/* Room group header */}
                      <tr key={`hdr-${room}`} className="bg-gray-50 border-t border-gray-200">
                        <td colSpan={9} className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          {t('owner.roomTenantCount', 'Room {{room}} · {{count}} tenants', { room, count: visible.length })}
                        </td>
                      </tr>
                      {visible.map(e => {
                        const pending = Number(e.amountDue) - Number(e.amountPaid);
                        return (
                          <tr key={e.id} className={`hover:bg-gray-50 cursor-pointer ${e.status === 'OVERDUE' ? 'bg-red-50/40' : ''}`} onClick={() => goToTenant(e.tenantId)}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {e.photoUrl
                                  ? <img src={e.photoUrl} className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-200" alt="" />
                                  : <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-sm shrink-0">{e.tenantName.charAt(0).toUpperCase()}</div>
                                }
                                <div>
                                  <p className="font-medium text-gray-900 text-sm">{e.tenantName}</p>
                                  <p className="text-xs text-gray-400">{e.tenantPhone}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-sm">{e.roomNumber ?? '—'}</td>
                            <td className="px-4 py-3 font-medium">{e.billingMonth}</td>
                            <td className="px-4 py-3 text-gray-500">{e.dueDate}</td>
                            <td className="px-4 py-3 text-right">{fmt(Number(e.amountDue))}</td>
                            <td className="px-4 py-3 text-right text-green-700">{fmt(Number(e.amountPaid))}</td>
                            <td className={`px-4 py-3 text-right font-medium ${pending > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {pending > 0 ? fmt(pending) : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[e.status]}`}>
                                {rentStatusLabel(t, e.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right" onClick={ev => ev.stopPropagation()}>
                              {(e.status === 'PENDING' || e.status === 'PARTIAL' || e.status === 'OVERDUE') && (
                                <button
                                  onClick={() => {
                                    setPayingId(e.id);
                                    setPayingTenantId(e.tenantId);
                                    setPayForm({
                                      amount: String(Number(e.amountDue) - Number(e.amountPaid)),
                                      paymentMethod: 'CASH',
                                      transactionRef: '',
                                    });
                                    setPayReceiptFile(null);
                                  }}
                                  className="text-teal-600 text-xs font-medium hover:underline"
                                >
                                  {t('rent.recordPayment')}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  );
                })}
              </tbody>
              {entries.length > 0 && (
                <tfoot className="bg-gray-50 text-sm font-semibold">
                  <tr>
                    <td className="px-4 py-3 text-gray-600" colSpan={4}>{t('owner.totals', 'Totals')}</td>
                    <td className="px-4 py-3 text-right">{fmt(entries.reduce((s, e) => s + Number(e.amountDue), 0))}</td>
                    <td className="px-4 py-3 text-right text-green-700">{fmt(entries.reduce((s, e) => s + Number(e.amountPaid), 0))}</td>
                    <td className="px-4 py-3 text-right text-red-600">{totalPending > 0 ? fmt(totalPending) : '—'}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
            )}
            <Pagination
              page={entriesPagination.page}
              totalPages={data?.totalPages ?? 0}
              totalElements={data?.totalElements ?? 0}
              size={entriesPagination.size}
              onPageChange={entriesPagination.setPage}
              className="px-4 pb-3"
            />
          </div>
        )}

        {/* ── Payment History Tab ── */}
        {tab === 'history' && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap items-end gap-4 px-4 py-3 border-b border-gray-50">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('owner.fromLabel', 'From')}</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => { setFromDate(e.target.value); historyPagination.resetPage(); }}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('owner.toLabel', 'To')}</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => { setToDate(e.target.value); historyPagination.resetPage(); }}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                />
              </div>
              <div className="ml-auto flex gap-4 text-sm">
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-center">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><IndianRupee size={11} /> {t('dashboard.collected')}</p>
                  <p className="font-bold text-green-700">{fmt(totalCollected)}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-center">
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Users size={11} /> {t('owner.payments', 'Payments')}</p>
                  <p className="font-bold text-blue-700">{paymentHistory.length}</p>
                </div>
              </div>
            </div>

            {historyLoading ? (
              <p className="text-gray-400 py-10 text-center text-sm">{t('common.loading')}</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">{t('owner.tenant', 'Tenant')}</th>
                    <th className="px-4 py-3 text-left">{t('common.room')}</th>
                    <th className="px-4 py-3 text-right">{t('common.amount')}</th>
                    <th className="px-4 py-3 text-left">{t('rent.paymentMethod')}</th>
                    <th className="px-4 py-3 text-left">{t('owner.paidAt', 'Paid At')}</th>
                    <th className="px-4 py-3 text-left">{t('owner.ref', 'Ref')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paymentHistory.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                        {t('owner.noPaymentsForRange', 'No payments found for the selected date range.')}
                      </td>
                    </tr>
                  )}
                  {pagedPaymentHistory.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => goToTenant(p.tenantId)}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{p.tenantName}</p>
                        <p className="text-xs text-gray-400">{p.tenantPhone}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.roomNumber ?? '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-green-700">{fmt(Number(p.amount))}</td>
                      <td className="px-4 py-3">
                        <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                          {paymentMethodLabel(t, p.paymentMethod)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(p.paidAt).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{p.transactionRef ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
                {paymentHistory.length > 0 && (
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-700" colSpan={2}>
                        {t('owner.totalPaymentsCount', 'Total ({{count}} payments)', { count: paymentHistory.length })}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">{fmt(totalCollected)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
            <Pagination
              page={historyPagination.page}
              totalPages={Math.ceil(paymentHistory.length / historyPagination.size) || 0}
              totalElements={paymentHistory.length}
              size={historyPagination.size}
              onPageChange={historyPagination.setPage}
              className="px-4 pb-3"
            />
          </div>
        )}

        {/* ── Pending Verifications Tab ── */}
        {tab === 'verifications' && (
          <PendingVerificationsPanel hostelId={hostelId} accentColor="teal" />
        )}
      </div>

      {/* Record payment modal */}
      {payingId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-chrome-page rounded-xl p-6 w-80 shadow-xl">
            <h3 className="font-semibold mb-4">{t('rent.recordPayment')}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.amount')} *</label>
                <input
                  type="number"
                  value={payForm.amount}
                  onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('rent.paymentMethod')}</label>
                <Select
                  value={payForm.paymentMethod}
                  onChange={e => setPayForm(p => ({ ...p, paymentMethod: e.target.value }))}
                  options={['CASH', 'UPI', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'CHEQUE', 'OTHER'].map(m => ({ value: m, label: paymentMethodLabel(t, m) }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('owner.transactionRef', 'Transaction / Reference ID')}{payRefRequired && ' *'}
                </label>
                <input
                  value={payForm.transactionRef}
                  onChange={e => setPayForm(p => ({ ...p, transactionRef: e.target.value }))}
                  placeholder={payRefRequired
                    ? t('owner.upiRefPlaceholderRequired', 'Enter unique UPI / bank transaction ID…')
                    : t('owner.upiRefPlaceholder', 'UPI ID / Ref number…')}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                {payRefRequired && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    {t('owner.transactionRefHint', 'Must be unique — the same reference cannot be used for two payments.')}
                  </p>
                )}
              </div>
              <ReceiptAttachField file={payReceiptFile} onChange={setPayReceiptFile} />
            </div>
            {payMutation.isError && (
              <p className="text-red-500 text-xs mt-2">{(payMutation.error as Error).message}</p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => payMutation.mutate({
                  id: payingId,
                  body: { amount: Number(payForm.amount), paymentMethod: payForm.paymentMethod, transactionRef: payForm.transactionRef || undefined },
                })}
                disabled={!payForm.amount || (payRefRequired && !payForm.transactionRef.trim()) || payMutation.isPending}
                className="flex-1 bg-teal-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40"
              >
                {payMutation.isPending ? t('owner.saving', 'Saving…') : t('common.save')}
              </button>
              <button onClick={() => { setPayingId(null); setPayReceiptFile(null); }} className="flex-1 border rounded-lg py-2 text-sm">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
